import { PInlineNotification } from "@porsche-design-system/components-react"
import type { CustomerAddressView, CustomerContactDetailsView } from "@slfinrtl/ui-bff-routes"
import { useEffect } from "react"
import { FormattedMessage } from "react-intl"
import { useCustomerContactDetails } from "src/hooks/cap/useCustomerContactDetails/useCustomerContactDetails"
import { Skeleton } from "../skeleton"
import { CustomerAddressList } from "./CustomerAddressList"
import { customerAddressListMessages } from "./customerAddressList.messages"

export interface SelectedCustomer {
	address: SelectedCustomerAddress
	emailAddress?: string
	mobileNumber?: string
	name: string
}

export interface SelectedCustomerAddress {
	city: string
	companyName1?: string
	companyName2?: string
	formatted: string[]
	houseNumber: string
	id?: string
	postalCode: string
	street: string
	telephoneNumber?: string
}

export interface CustomerAddressListContainerProps {
	businessPartnerId: string
	disabled?: boolean
	/**
	 * called with the customer and their initially selected address on load
	 * and again on every user selection. Must be referentially stable
	 * (e.g. useCallback), otherwise the initial selection is re-reported
	 * on every render.
	 */
	onChange: (customer: SelectedCustomer) => void
}

/**
 * Loads the contact details of a business partner and renders their addresses as a selectable list.
 */
export const CustomerAddressListContainer = ({
	businessPartnerId,
	disabled,
	onChange,
}: CustomerAddressListContainerProps) => {
	const customerContactDetailsResponse = useCustomerContactDetails(businessPartnerId)
	const contactDetails = customerContactDetailsResponse?.value

	// report the customer with the initially selected address on load
	useEffect(() => {
		const selectedAddress = contactDetails?.addresses.find((address) => address.selected)
		if (contactDetails && selectedAddress) {
			onChange(toSelectedCustomer(contactDetails, selectedAddress))
		}
	}, [contactDetails, onChange])

	if (customerContactDetailsResponse?.hasError) {
		return (
			<PInlineNotification state="error" dismissButton={false}>
				<FormattedMessage {...customerAddressListMessages.loadingError} />
			</PInlineNotification>
		)
	}

	if (!contactDetails) {
		return <Skeleton.RadioGroup count={2} />
	}

	return (
		<CustomerAddressList
			addresses={contactDetails.addresses}
			disabled={disabled}
			onChange={(address) => onChange(toSelectedCustomer(contactDetails, address))}
		/>
	)
}

const toSelectedCustomer = (
	contactDetails: CustomerContactDetailsView,
	address: CustomerAddressView,
): SelectedCustomer => ({
	address: {
		city: address.raw.city,
		companyName1: address.raw.companyName1,
		companyName2: address.raw.companyName2,
		formatted: address.formatted,
		houseNumber: address.raw.houseNumber,
		id: address.id,
		postalCode: address.raw.postalCode,
		street: address.raw.street,
		telephoneNumber: address.raw.telephoneNumber,
	},
	emailAddress: contactDetails.emailAddress,
	mobileNumber: contactDetails.mobileNumber,
	name: contactDetails.name,
})
